import React, {useState} from "react"
import clsx from "clsx"
import {useForm} from "react-hook-form"
import {Alert, AlertIcon, Button, Flex, IconButton} from "@chakra-ui/react"
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd"
import {PencilSquareIcon, TrashIcon} from "@heroicons/react/20/solid"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {IconName} from "@fortawesome/pro-regular-svg-icons"

import {VoteButtonType} from "@backend/types"
import {FIconColorPicker, FormInput, FSwitch} from "@components/form"
import {FieldType} from "@components/types"


const fields: FieldType = {
    icon: {
        field: "icon",
        label: "Icon",
        rules: {
            required: "Icon is required"
        }
    },
    color: {
        field: "color",
        label: "Color",
        rules: {
            required: "Color is required"
        }
    },
    key: {
        field: "key",
        label: "Keyword",
        rules: {
            required: "Keyword is required"
        }
    },
    text: {
        field: "text",
        label: "Text",
        rules: {
            required: "Text is required"
        }
    },
    askReason: {
        field: "askReason",
        label: "Ask for Reason"
    }
}

const getListStyle = isDraggingOver => ({
    background: isDraggingOver ? "lightblue" : null
})

const reorder = (list: VoteButtonType[], startIndex: number, endIndex: number): VoteButtonType[] => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
}

type PlayButtonsEditorProps = {
    voteButtons?: VoteButtonType[],
    onSelectedChange: (any) => void
}

export function PlayButtonsEditor({voteButtons, onSelectedChange}: PlayButtonsEditorProps) {

    const [items, setItems] = useState<VoteButtonType[]>(voteButtons || [])
    const [selectedButtonIndex, setSelectedButtonIndex] = useState<number>(-1)

    const {
        handleSubmit,
        register,
        setValue,
        setError,
        clearErrors,
        watch,
        trigger,
        reset,
        getValues,
        formState: {
            errors,
            isSubmitting
        }
    } = useForm({})

    async function onSubmit(values) {

        const btn: VoteButtonType = {
            key: values.key,
            icon: values.icon,
            colorScheme: values.color.split(".")[0],
            bgColor: values.color,
            text: values.text,
            askReason: values.askReason
        }


        if (selectedButtonIndex > -1) {
            items.splice(selectedButtonIndex, 1, btn)
            setSelectedButtonIndex(-1)
        } else {
            items.splice(items.length, 0, btn)
        }


        setItems([...items])

        reset({
            key: "",
            icon: undefined,
            colorScheme: "",
            bgColor: "",
            text: "",
            askReason: false
        })
        onSelectedChange({voteButtons: items})

    }

    function onDragEnd(result) {
        // dropped outside the list
        if (!result.destination) {
            return
        }

        const orderedItems = reorder(
            items,
            result.source.index,
            result.destination.index
        )

        setItems(orderedItems)
        onSelectedChange({voteButtons: [...orderedItems]})
    }

    function onRemoveClick(btnIndex: number) {
        items.splice(btnIndex, 1)
        setItems([...items])
        onSelectedChange({voteButtons: [...items]})
    }

    function onEditClick(btnIndex: number) {
        const btn = items[btnIndex]
        setSelectedButtonIndex(btnIndex)

        reset({
            ...btn,
            color: btn.bgColor || `${btn.colorScheme}.500`
        })
    }

    // @ts-ignore
    const watchValues = watch([fields.icon.field, fields.color.field])
    const watchReason = watch(fields.askReason.field, false)

    return <div className="grid grid-cols-2 gap-4">

        <form onSubmit={handleSubmit(onSubmit)} className="col-span-2 lg:col-span-1 px-4 space-y-4">
            <h2 className="text-lg font-bold mb-4 text-gray-800">Create New Button</h2>
            <div className="col-span-full sm:col-span-1 flex items-end space-x-4">
                <FIconColorPicker
                    register={register}
                    errors={errors}
                    icon={{
                        ...fields.icon,
                        setValue,
                        value: watchValues[0]
                    }}
                    color={{
                        ...fields.color,
                        setValue,
                        value: watchValues[1]
                    }}
                />
            </div>
            <div className="col-span-full sm:col-span-2">
                <FormInput register={register} errors={errors} {...fields.text}/>
            </div>
            <div className="col-span-full sm:col-span-2">
                <FormInput register={register} errors={errors} {...fields.key}/>
            </div>
            <div className="col-span-full sm:col-span-2">
                <FSwitch value={watchReason} register={register} errors={errors} {...fields.askReason} />
            </div>

            <div className="col-span-full sm:col-span-1 flex items-end">
                <Button w="full" colorScheme="blue" aria-label="submit" type="submit" onClick={handleSubmit(onSubmit)}>
                    {selectedButtonIndex > -1 ? "Update Button" : "Add Button"}
                </Button>
            </div>
        </form>

        <div className="col-span-2 lg:col-span-1">
            <h2 className="text-lg font-bold mb-4 text-gray-800">Buttons</h2>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                        <ul role="list"
                            className="grid grid-cols-1 gap-5 sm:grid-cols-2 p-2"
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={getListStyle(snapshot.isDraggingOver)}>
                            {items.map((btn, index) => (
                                <Draggable key={btn.key} draggableId={btn.key} index={index}>
                                    {(provided, snapshot) => (
                                        <li
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className="col-span-2 flex rounded-md shadow-sm">
                                            <Flex
                                                bg={btn.bgColor || `${btn.colorScheme}.500`}
                                                className={clsx(
                                                    "w-16 flex-shrink-0 items-center justify-center rounded-l-md text-white"
                                                )}
                                            >
                                                <FontAwesomeIcon size="lg" icon={["fal", btn.icon as IconName]}/>
                                            </Flex>
                                            <div
                                                className="flex flex-1 items-center justify-between truncate rounded-r-md border-b border-r border-t border-gray-200 bg-white">
                                                <div className="flex-1 truncate px-4 py-2 text-sm">
                                                    <a
                                                        className="font-medium text-gray-900 hover:text-gray-600">
                                                        {btn.text}
                                                    </a>
                                                    <p className="text-gray-500">{btn.key}</p>
                                                </div>
                                                <div className="space-x-2 pr-2">
                                                    <IconButton variant="ghost"
                                                                aria-label=""
                                                                color="gray.300"
                                                                onClick={onEditClick.bind(null, index)}
                                                                icon={<PencilSquareIcon className="h-5 w-5"/>}/>
                                                    <IconButton variant="ghost"
                                                                aria-label=""
                                                                color="gray.300"
                                                                onClick={onRemoveClick.bind(null, index)}
                                                                icon={<TrashIcon className="h-5 w-5"/>}/>
                                                </div>
                                            </div>
                                        </li>)}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </ul>
                    )}
                </Droppable>
            </DragDropContext>
        </div>


        {Object.keys(errors).length > 0 &&
          <Alert status="error">
            <AlertIcon/>
            <ul>
                {Object.values(errors).map((x, i) => <li key={i}>
                    {x.message as string}
                </li>)}
            </ul>
          </Alert>
        }
    </div>
}